import {
  PILLAR_HEADINGS,
  PILLAR_LABELS,
  PILLAR_ONE,
  PILLAR_TILES,
  type Pillar,
} from '@hi/contracts/shared'
import { cn } from '../../lib/cn.js'

export interface PillarWordProps {
  /** The value the API sends — `mind_body`, `mentorship`. */
  pillar: Pillar | null | undefined
  /**
   * Which of the four names. A pillar is called four things and they are not
   * interchangeable.
   *
   * `tab` is the one word (*Mentors*), `tile` the longer one Home has room for
   * (*Health & Mind*), `heading` the sentence over a list (*Mentors who have been
   * there*), and `one` the singular for *"No mentor in Vancouver lists…"*.
   */
  form?: 'tab' | 'tile' | 'heading' | 'one'
  empty?: string
  className?: string
}

const WORDS = {
  tab: PILLAR_LABELS,
  tile: PILLAR_TILES,
  heading: PILLAR_HEADINGS,
  one: PILLAR_ONE,
} as const

/**
 * A pillar, written as the word the product uses for it.
 *
 * The desk shows a pillar on every row of the Categories screen — `group_key`,
 * and now `refers_to_pillar` beside it — and the API sends a code. Without this
 * the screen turns `mind_body` into a label itself, which is exactly the failure
 * that put **"Vetted mentors providers"** on a board: a sentence assembled out of
 * a noun that was chosen to be a tab.
 *
 * The words come from `@hi/contracts/shared`, the same file the phone app and the
 * API read, so the desk cannot call a pillar something the product does not.
 *
 * This is the pair to `StatusWord` and deliberately not part of it: a pillar is
 * not a state. It has no tone, nothing transitions into it, and putting it there
 * would make `StatusWord` mean "any code with a word", which is how a component
 * stops being about anything.
 */
export function PillarWord({ pillar, form = 'tab', empty = '–', className }: PillarWordProps) {
  if (!pillar) return <span className={cn('text-ink-muted', className)}>{empty}</span>
  return <span className={className}>{WORDS[form][pillar]}</span>
}
